/** The landing (design v2 screen 1, spec §6.2): the hero on --room with the desk, three
 * proofs, how it works, what you should know. No exam board is named on it (docs/decisions/
 * 2026-09-30-no-exam-board-marks-in-marketing.md); no prices;
 * the hero's action depends on the trial flag. Every proof is the app's own component over
 * the app's own fixture — a real chapter's deck, the format the account really learned —
 * and the paper, which is written in the generator's shape, is never called real. */
import { useState } from "react";
import { brand } from "../brand";
import { StudyView } from "../cards/StudyView";
import { sampleDeck } from "../fixtures/sampleDeck";
import { samplePaper } from "../fixtures/samplePaper";
import { sampleProfile } from "../fixtures/sampleProfile";
import { questionMarks } from "../paper/marks";
import { PaperView } from "../paper/PaperView";
import type { Paper } from "../paper/types";
import { savePendingCode } from "./pendingCode";
import "../styles/cards.css";
import "../styles/paper.css";

// Two questions of the sample paper, with the cover's total recomputed from them: a slice
// that kept the whole paper's total would fail its own reconciliation, and the landing
// would open by accusing the product of not adding up.
const shown = samplePaper.questions.slice(1, 3);
const fragment: Paper = {
  ...samplePaper,
  questions: shown,
  total_marks: shown.reduce((n, q) => n + questionMarks(q), 0),
};
const BOARD = "Computer Science · Paper 1 style";

/** The hero's desk: a paper, a chapter's progress slip and a card, overlapping on purpose.
 * One object scaled with container units; decorative, so hidden from the reading order. */
function Desk() {
  const q1 = samplePaper.questions[0]!;
  const q3 = samplePaper.questions[2]!;
  const card = sampleDeck.cards.find((c) => c.front === "Byte") ?? sampleDeck.cards[0]!;
  const known = 7;
  const total = 12;
  return (
    <div className="desk" aria-hidden="true">
      <div className="desk__paper">
        <div className="desk__cover">
          <span className="desk__board">{BOARD}</span>
          <span className="desk__title">{samplePaper.title.replace(/^Practice Paper — /, "")}</span>
          <span className="desk__time">Time allowed: {samplePaper.duration_minutes} minutes</span>
        </div>
        <div className="desk__q">
          <span className="desk__n">01</span>
          <div>
            <p>{q1.stem}</p>
            <div className="desk__options">
              {q1.options.map((o, i) => (
                <span key={o} className={o === "8" ? "desk__opt desk__opt--on" : "desk__opt"}>
                  <span className="desk__lozenge" /> {String.fromCharCode(65 + i)} {o}
                </span>
              ))}
            </div>
          </div>
          <span className="desk__mark">[{q1.marks}]</span>
        </div>
        <div className="desk__q">
          <span className="desk__n">03</span>
          <div>
            <p>{q3.stem}</p>
            <span className="desk__line" />
            <span className="desk__line" />
            <span className="desk__line" />
          </div>
          <span className="desk__mark">[{q3.marks}]</span>
        </div>
      </div>
      <div className="desk__slip">
        <div className="desk__slip-head">
          <span>
            {sampleDeck.number}. {sampleDeck.title}
          </span>
          <strong>
            {known} of {total} known
          </strong>
        </div>
        <div className="desk__strip">
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className={
                i < known
                  ? "desk__cell desk__cell--known"
                  : i < 9
                    ? "desk__cell desk__cell--again"
                    : "desk__cell"
              }
            />
          ))}
        </div>
      </div>
      <div className="desk__card">
        <div className="desk__card-head">
          <span>Definition</span>
          <span>Chapter {sampleDeck.number}</span>
        </div>
        <span className="desk__card-front">{card.front}</span>
        <span className="desk__card-hint">Tap the card to reveal</span>
      </div>
    </div>
  );
}

function CodeBlock({ onSignIn }: { onSignIn: () => void }) {
  const [code, setCode] = useState("");
  return (
    <form
      className="hero__code"
      onSubmit={(e) => {
        e.preventDefault();
        savePendingCode(code.trim());
        onSignIn();
      }}
    >
      <label htmlFor="landing-code" className="hero__code-label">
        {brand.name} is in a closed beta. Have an access code?
      </label>
      <div className="hero__code-row">
        <input
          id="landing-code"
          aria-label="Access code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder="XXXX - XXXX - XXXX"
        />
        <button type="submit" className="button" disabled={!code.trim()}>
          Redeem
        </button>
      </div>
      <p className="hero__code-note">
        Would like one? Write to <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
      </p>
    </form>
  );
}

const STEPS = [
  `Upload your textbook. ${brand.name} finds the chapters and shows you its evidence; you check the list before anything else happens.`,
  "Every chapter becomes flashcards, and a coverage report says what the book does and does not cover.",
  `Upload a past paper. ${brand.name} learns the format; you confirm it.`,
  "Ask for a paper on the chapters you choose. Each is written, checked for accuracy, and comes with its mark scheme.",
];

const KNOW: [string, string][] = [
  [
    "Your book is an input, never an output.",
    "What you upload is never shown to anyone — not shared, not offered for download, not even to you. Only material made from it is.",
  ],
  [
    "Generated papers are practice, not the real thing.",
    "Every paper carries that disclaimer, and a paper whose marks do not add up says so on its face.",
  ],
  [
    "Usage is metered and the meter is on screen.",
    "Making this material costs real money; you always see how much of your allowance is left.",
  ],
  ["No advertising. No tracking.", "No third-party analytics, ever."],
];

export function Landing({ trialOpen, onSignIn }: { trialOpen: boolean; onSignIn: () => void }) {
  const p = sampleProfile;
  return (
    <main className="landing">
      <section className="hero">
        <header className="hero__nav">
          <span className="hero__wordmark">{brand.wordmark}</span>
          {/* Row-reverse and wrap in a fixed height: links drop out on a phone, Sign in stays. */}
          <nav aria-label="Site" className="hero__links">
            <a
              href="/subjects"
              className="hero__signin"
              onClick={(e) => {
                e.preventDefault();
                onSignIn();
              }}
            >
              Sign in
            </a>
            <a href="#know">What you should know</a>
            <a href="#how">How it works</a>
          </nav>
        </header>
        <div className="hero__grid">
          <div className="hero__text">
            <p className="hero__eyebrow">Any subject · any exam board</p>
            <h1 className="hero__title">
              Revise from the book <em>you already have.</em>
            </h1>
            <p className="hero__lede">
              Upload the book you already study from. {brand.name} reads it, checks the chapters
              with you, and gives you flashcards for every one — then learns your exam board&rsquo;s
              format from a past paper and writes you original practice papers with mark schemes.
            </p>
            {trialOpen ? (
              <button className="button hero__start" onClick={onSignIn}>
                Start a 7-day trial
              </button>
            ) : (
              <CodeBlock onSignIn={onSignIn} />
            )}
          </div>
          <Desk />
        </div>
        <div className="hero__captions">
          <p>
            Shown above: cards from chapter {sampleDeck.number} of a real scanned textbook, and
            questions in the style {brand.name} learned from one past paper.
          </p>
          <p>Generated practice material. Not a past paper, and not endorsed by any exam board.</p>
        </div>
      </section>

      <section className="proofs" aria-labelledby="proofs-title">
        <p className="eyebrow">What it makes</p>
        <h2 id="proofs-title" className="section-title">
          Three things from one chapter, <em>exactly as the app shows them.</em>
        </h2>
        <div className="proofs__grid">
          <section className="proof" aria-label="Flashcards">
            <div className="proof__study">
              <StudyView deck={sampleDeck} headingLevel={2} />
            </div>
            <h3>Flashcards from your chapter</h3>
            <p>
              Chapter {sampleDeck.number}, {sampleDeck.title} — {sampleDeck.cards.length} cards from
              one real textbook chapter. Phone-sized, for the night before.
            </p>
          </section>

          <section className="proof" aria-label="Practice paper">
            <div className="proof__paper">
              <PaperView paper={fragment} board={BOARD} headingLevel={2} />
            </div>
            <h3>A practice paper that looks like a paper</h3>
            <p>
              Original questions in your board&rsquo;s style, with a mark scheme you can check
              against. Two questions in the shape the generator writes them.
            </p>
          </section>

          <section className="proof" aria-label="Exam format">
            <div className="proof__format">
              <span className="proof__format-board">Learned from one past paper</span>
              <span className="proof__format-title">{p.paper_title}</span>
              <dl>
                <div>
                  <dt>Total</dt>
                  <dd>{p.total_marks} marks</dd>
                </div>
                <div>
                  <dt>Time</dt>
                  <dd>{p.duration_minutes} minutes</dd>
                </div>
                <div>
                  <dt>Per question</dt>
                  <dd>
                    {p.per_question_range[0]} to {p.per_question_range[1]} marks
                  </dd>
                </div>
                <div>
                  <dt>Examples kept</dt>
                  <dd>{p.exemplars} questions</dd>
                </div>
              </dl>
              <span className="proof__chips">
                {p.command_words.map((w) => (
                  <span key={w} className="proof__chip">
                    {w}
                  </span>
                ))}
              </span>
            </div>
            <h3>It learns your exam&rsquo;s format from one past paper</h3>
            <p>
              From a single past paper it learned the command words, the {p.total_marks} marks in{" "}
              {p.duration_minutes} minutes and questions of {p.per_question_range[0]} to{" "}
              {p.per_question_range[1]} marks, and kept {p.exemplars} questions, each traced to its
              page. You check the format before any paper is written.
            </p>
          </section>
        </div>
      </section>

      <section id="how" className="how" aria-labelledby="how-title">
        <div className="how__head">
          <p className="eyebrow">How it works</p>
          <h2 id="how-title" className="section-title">
            You check its work <em>before anything is spent.</em>
          </h2>
        </div>
        <ol className="how__steps">
          {STEPS.map((s, i) => (
            <li key={i}>
              <span className="how__n">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section id="know" className="know" aria-labelledby="know-title">
        <div className="know__inner">
          <h2 id="know-title" className="eyebrow">
            What you should know
          </h2>
          <ul className="know__grid">
            {KNOW.map(([lead, body]) => (
              <li key={lead}>
                <strong className="know__lead">{lead}</strong>
                <span>{body}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
